import { ChangeEvent, ReactElement, RefObject } from "react";

import { Checkbox, type SvgComponentType } from "@newsela/angelou";
import ReactDOM from "react-dom";

import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import { CSSObject } from "@emotion/react";
import { useSearchAdminPermissions } from "@/search/Langston/useSearchAdminPermissions";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { IContentValues } from "@/search/Langston/types/IContentValues";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { checkboxCss } from "./InstructionalFeatureCheckbox.styles";

const CheckboxWithTrackEvent = withTrackEvent(Checkbox);

const defaultLanguageValue = LangstonParameterSchema.languages.default;

export const InstructionalFeatureCheckbox = ({
  paramName,
  currentRef,
  isDomReady,
  IconComponent,
  option,
  targetElement,
}: IInstructionalFeatureCheckbox) => {
  const {
    getters: { isDisabledFilters },
    handlers: {
      hasSearchParamValue,
      toggleSearchParamValue,
      setSearchParamValue,
    },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  const onChange = (
    e: ChangeEvent<HTMLInputElement>,
    option: IContentValues,
  ) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    // Handle language selection separately since it's not
    // an instructional feature in Langston.
    if (paramName === LangstonParameterSchema.languages.key) {
      const newVal = e.target.checked ? option.value : defaultLanguageValue;
      setSearchParamValue(paramName, newVal);
      return;
    }

    toggleSearchParamValue(paramName, option.value);
  };

  const eventTrackingDescription = `Toggle ${hasSearchParamValue(paramName, option.value) ? "off" : "on"} the ${option.label} instructional feature filter in search`;

  return (
    <>
      <CheckboxWithTrackEvent
        __cssFor={checkboxCss(option?.overrideStyles || {})}
        checked={hasSearchParamValue(paramName, option.value)}
        label={option.label}
        disabled={isDisabledFilters}
        onChange={(e) => onChange(e, option)}
        value={option.value}
        inputRef={currentRef}
        dataProps={{
          "data-qa-selector": `search-instructional-filter-${option.label.replaceAll(" ", "-")}`,
        }}
        actionName={actionNames.filter}
        objectType={objectTypes.content}
        streamProperties={{
          description: eventTrackingDescription,
        }}
      />
      {isDomReady &&
        targetElement &&
        ReactDOM.createPortal(IconComponent, targetElement)}
    </>
  );
};

interface IInstructionalFeatureCheckbox {
  paramName: string;
  currentRef: RefObject<HTMLInputElement>;
  isDomReady: boolean;
  IconComponent: ReactElement;
  option: {
    label: string;
    value: string;
    dataQaSelector: string;
    SvgComponent: SvgComponentType;
    paramName?: string;
    overrideStyles?: CSSObject;
  };
  targetElement: Element;
}
